import { useState, useEffect } from "react";
import { ArrowRight, ArrowLeft, Loader2, CheckCircle, Send, Sparkles, X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUSINESS_TYPES = [
  "Restaurant / Cafe", "Gym / Fitness", "Salon / Spa", "Medical / Clinic",
  "Law Firm", "Real Estate", "School / Coaching", "Photography",
  "Hotel / Resort", "Travel / Tourism", "Retail / Shop", "E-Commerce",
  "Freelancer / Portfolio", "NGO / Non-Profit", "Other"
];

const PAGE_OPTIONS = [
  "Home", "About Us", "Services", "Products / Menu", "Gallery / Portfolio",
  "Team / Staff", "Testimonials", "Pricing", "Blog", "Contact", "FAQ",
  "Booking / Appointment", "Careers"
];

const FEATURE_OPTIONS = [
  "E-Commerce / Online Shop", "Contact Form with Backend", "Image Gallery Section",
  "SEO Setup & Optimization", "Social Media Integration", "Blog / News Section",
  "Admin Panel / CMS", "Booking / Appointment System", "WhatsApp Chat Button",
  "Google Maps Integration", "Multi-Language (Hindi/English)", "Newsletter / Email Signup",
  "Live Chat Widget", "Customer Reviews System", "Payment Gateway (Razorpay/UPI)",
  "AI Chatbot", "PWA (Install as App)", "Speed Optimization Package",
  "Analytics Dashboard", "Video Background / Hero Reel", "Lead Capture Popup",
  "Login / User Accounts"
];

const STEPS = [
  { id: "intro", title: "Let's Get Started" },
  { id: "you", title: "About You" },
  { id: "business", title: "Your Business" },
  { id: "goals", title: "Website Goals" },
  { id: "pages", title: "Pages & Features" },
  { id: "design", title: "Design & Extras" },
  { id: "budget", title: "Budget & Timeline" },
  { id: "analyzing", title: "AI Analysis" },
  { id: "result", title: "Your Proposal" },
];

export default function GetStartedWizard({ onClose }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    name: "", email: "", phone: "", businessName: "", businessType: [], location: "",
    whatYouDo: "", targetCustomers: "", currentPresence: [],
    websiteGoal: [], pagesNeeded: [], features: [], hasLogo: "no",
    hasDomain: "no", designStyle: [], referenceWebsites: "",
    budget: "", timeline: "", additionalNotes: ""
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const update = (key, val) => setAnswers(prev => ({ ...prev, [key]: val }));
  const toggleArray = (key, val) => {
    setAnswers(prev => ({
      ...prev,
      [key]: prev[key].includes(val) ? prev[key].filter(v => v !== val) : [...prev[key], val]
    }));
  };

  const canProceed = () => {
    if (step === 1) return answers.name.trim();
    if (step === 2) return answers.businessType.length > 0;
    if (step === 3) return answers.websiteGoal.length > 0;
    return true;
  };

  const analyze = async () => {
    setStep(7);
    setLoading(true);
    setError("");
    // Flatten multi-select arrays to comma-joined strings for the backend prompt/emails
    const payload = {
      ...answers,
      businessType: answers.businessType.join(", "),
      websiteGoal: answers.websiteGoal.join(" + "),
      currentPresence: answers.currentPresence.join(", ") || "nothing",
      designStyle: answers.designStyle.join(" + "),
    };
    try {
      const { data } = await axios.post(`${API}/wizard/analyze`, { answers: payload });
      setResult(data);
      setStep(8);
    } catch {
      setError("Something went wrong. Please try again.");
      setStep(6);
    }
    setLoading(false);
  };

  const goNext = () => {
    if (step === 6) { analyze(); return; }
    if (step < STEPS.length - 1) setStep(step + 1);
  };
  const goBack = () => { if (step > 0 && step !== 7 && step !== 8) setStep(step - 1); };

  const sendWhatsApp = (msg) => {
    window.open(`https://wa.me/919242478118?text=${encodeURIComponent(msg)}`, "_blank");
  };

  // Lock body scroll + ESC-to-close
  useEffect(() => {
    document.body.classList.add("no-scroll");
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("no-scroll");
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4 animate-fadeIn"
      data-testid="wizard-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-2xl h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-y-auto relative">
        <button onClick={onClose} data-testid="wizard-close" className="tap touch-target inline-flex items-center justify-center absolute top-2 right-2 text-zinc-400 hover:text-white z-10 rounded-sm active:bg-white/5">
          <X size={22} />
        </button>

        {/* Progress bar */}
        {step < 8 && (
          <div className="h-1 bg-white/5">
            <div className="h-full bg-red-600 transition-all duration-500" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
          </div>
        )}

        <div className="p-6 sm:p-8">
          {/* Step 0: Intro */}
          {step === 0 && (
            <div className="text-center py-8" data-testid="wizard-step-intro">
              <div className="w-16 h-16 bg-red-600/10 flex items-center justify-center mx-auto mb-6">
                <Sparkles size={28} className="text-red-500" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight mb-4">
                Get Your <span className="text-red-600">Website</span>
              </h2>
              <p className="text-sm text-zinc-400 max-w-md mx-auto mb-2 leading-relaxed">
                Don't know where to start? No worries! Answer a few simple questions and our AI will create a custom website proposal for your business.
              </p>
              <p className="text-xs text-zinc-600 mb-8">Takes about 2 minutes. No technical knowledge needed.</p>
              <button onClick={goNext} data-testid="wizard-start-btn" className="bg-red-600 text-white font-bold uppercase tracking-widest py-4 px-10 text-sm hover:bg-white hover:text-black transition-all duration-300">
                Let's Go <ArrowRight className="inline ml-2" size={16} />
              </button>
            </div>
          )}

          {/* Step 1: About You */}
          {step === 1 && (
            <div data-testid="wizard-step-you">
              <p className="text-[10px] text-red-600 uppercase tracking-[0.2em] mb-2">Step 1 of 6</p>
              <h3 className="text-xl font-black uppercase tracking-tight mb-6">Tell Us About <span className="text-red-600">You</span></h3>
              <div className="space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Your Name *</label>
                  <input data-testid="wizard-name" value={answers.name} onChange={e => update("name", e.target.value)} placeholder="e.g. Rahul Sharma" autoComplete="name" enterKeyHint="next"
                    className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none transition-colors" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Phone / WhatsApp Number</label>
                  <input data-testid="wizard-phone" value={answers.phone} onChange={e => update("phone", e.target.value)} placeholder="e.g. +91 98765 43210" type="tel" inputMode="tel" autoComplete="tel" enterKeyHint="next"
                    className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none transition-colors" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Email <span className="text-zinc-700">(to receive your proposal)</span></label>
                  <input data-testid="wizard-email" value={answers.email} onChange={e => update("email", e.target.value)} type="email" inputMode="email" autoComplete="email" enterKeyHint="next" placeholder="e.g. rahul@gmail.com"
                    className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none transition-colors" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Your City / Location</label>
                  <input value={answers.location} onChange={e => update("location", e.target.value)} placeholder="e.g. Mumbai, Kolkata, Bolpur" autoComplete="address-level2" enterKeyHint="done"
                    className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none transition-colors" />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Your Business */}
          {step === 2 && (
            <div data-testid="wizard-step-business">
              <p className="text-[10px] text-red-600 uppercase tracking-[0.2em] mb-2">Step 2 of 6</p>
              <h3 className="text-xl font-black uppercase tracking-tight mb-6">Your <span className="text-red-600">Business</span></h3>
              <div className="space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Business Name</label>
                  <input value={answers.businessName} onChange={e => update("businessName", e.target.value)} placeholder="e.g. Sharma Dental Clinic"
                    className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none transition-colors" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">What type of business? * <span className="text-zinc-700 normal-case tracking-normal">(select all that apply)</span></label>
                  <div className="flex flex-wrap gap-2">
                    {BUSINESS_TYPES.map(t => (
                      <button key={t} onClick={() => toggleArray("businessType", t)}
                        className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border transition-all ${answers.businessType.includes(t) ? "bg-red-600 text-white border-red-600" : "border-white/10 text-zinc-400 hover:border-red-600/50"}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">What do you sell or offer? <span className="text-zinc-700">(in simple words)</span></label>
                  <textarea value={answers.whatYouDo} onChange={e => update("whatYouDo", e.target.value)} rows={2} placeholder="e.g. We sell homemade cakes and pastries, We are a dentist clinic offering teeth cleaning and braces"
                    className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none resize-none transition-colors" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Who are your customers?</label>
                  <input value={answers.targetCustomers} onChange={e => update("targetCustomers", e.target.value)} placeholder="e.g. Young families in my area, Students preparing for exams"
                    className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none transition-colors" />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Goals */}
          {step === 3 && (
            <div data-testid="wizard-step-goals">
              <p className="text-[10px] text-red-600 uppercase tracking-[0.2em] mb-2">Step 3 of 6</p>
              <h3 className="text-xl font-black uppercase tracking-tight mb-6">Website <span className="text-red-600">Goals</span></h3>
              <div className="space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">What's the main reason you want a website? * <span className="text-zinc-700 normal-case tracking-normal">(select all that apply)</span></label>
                  <div className="space-y-2">
                    {["Show my business to more people online", "Get more customers / leads", "Let customers book appointments or order online", "Sell products online (e-commerce)", "Just need a professional online presence", "Compete with other businesses that have websites"].map(g => (
                      <button key={g} onClick={() => toggleArray("websiteGoal", g)}
                        className={`w-full text-left px-4 py-3 text-xs font-bold border transition-all ${answers.websiteGoal.includes(g) ? "bg-red-600/10 text-red-500 border-red-600" : "border-white/10 text-zinc-400 hover:border-red-600/30"}`}>
                        {g}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Do you currently have anything online? <span className="text-zinc-700 normal-case tracking-normal">(select all that apply)</span></label>
                  <div className="flex flex-wrap gap-2">
                    {[["nothing", "Nothing yet"], ["social", "Social media only"], ["old_website", "An old website"], ["google", "Google My Business"]].map(([v, l]) => (
                      <button key={v} onClick={() => toggleArray("currentPresence", v)}
                        className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border transition-all ${answers.currentPresence.includes(v) ? "bg-red-600 text-white border-red-600" : "border-white/10 text-zinc-400 hover:border-red-600/50"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Pages & Features */}
          {step === 4 && (
            <div data-testid="wizard-step-pages">
              <p className="text-[10px] text-red-600 uppercase tracking-[0.2em] mb-2">Step 4 of 6</p>
              <h3 className="text-xl font-black uppercase tracking-tight mb-6">Pages & <span className="text-red-600">Features</span></h3>
              <div className="space-y-5">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Which pages do you want? <span className="text-zinc-700">(select all that apply)</span></label>
                  <div className="flex flex-wrap gap-2">
                    {PAGE_OPTIONS.map(p => (
                      <button key={p} onClick={() => toggleArray("pagesNeeded", p)}
                        className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border transition-all ${answers.pagesNeeded.includes(p) ? "bg-red-600 text-white border-red-600" : "border-white/10 text-zinc-400 hover:border-red-600/50"}`}>
                        {p}
                      </button>
                    ))}
                  </div>
                  <p className="text-[9px] text-zinc-600 mt-2">Don't worry if you're not sure — we'll recommend the right pages for your business.</p>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Any special features?</label>
                  <div className="flex flex-wrap gap-2">
                    {FEATURE_OPTIONS.map(f => (
                      <button key={f} onClick={() => toggleArray("features", f)}
                        className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border transition-all ${answers.features.includes(f) ? "bg-red-600 text-white border-red-600" : "border-white/10 text-zinc-400 hover:border-red-600/50"}`}>
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Design */}
          {step === 5 && (
            <div data-testid="wizard-step-design">
              <p className="text-[10px] text-red-600 uppercase tracking-[0.2em] mb-2">Step 5 of 6</p>
              <h3 className="text-xl font-black uppercase tracking-tight mb-6">Design & <span className="text-red-600">Extras</span></h3>
              <div className="space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">What style do you prefer? <span className="text-zinc-700 normal-case tracking-normal">(select all that apply)</span></label>
                  <div className="flex flex-wrap gap-2">
                    {["Modern & Clean", "Bold & Colorful", "Elegant & Luxury", "Simple & Minimal", "Fun & Playful", "Not sure — you decide!"].map(s => (
                      <button key={s} onClick={() => toggleArray("designStyle", s)}
                        className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border transition-all ${answers.designStyle.includes(s) ? "bg-red-600 text-white border-red-600" : "border-white/10 text-zinc-400 hover:border-red-600/50"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Any website you like? <span className="text-zinc-700">(paste links)</span></label>
                  <input value={answers.referenceWebsites} onChange={e => update("referenceWebsites", e.target.value)} placeholder="e.g. www.example.com (or leave empty)"
                    className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none transition-colors" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Do you have a logo?</label>
                  <div className="flex gap-2">
                    {[["yes", "Yes"], ["no", "No"], ["need", "I need one"]].map(([v, l]) => (
                      <button key={v} onClick={() => update("hasLogo", v)}
                        className={`px-4 py-2 text-[10px] font-bold uppercase tracking-wider border transition-all ${answers.hasLogo === v ? "bg-red-600 text-white border-red-600" : "border-white/10 text-zinc-400 hover:border-red-600/50"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Do you have a domain name? <span className="text-zinc-700">(like yourbusiness.com)</span></label>
                  <div className="flex gap-2">
                    {[["yes", "Yes"], ["no", "No"], ["what", "What's that?"]].map(([v, l]) => (
                      <button key={v} onClick={() => update("hasDomain", v)}
                        className={`px-4 py-2 text-[10px] font-bold uppercase tracking-wider border transition-all ${answers.hasDomain === v ? "bg-red-600 text-white border-red-600" : "border-white/10 text-zinc-400 hover:border-red-600/50"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                  {answers.hasDomain === "what" && <p className="text-[10px] text-zinc-500 mt-2 leading-relaxed">A domain is your website's address (like google.com). It costs Rs 500-1,500/year. Don't worry — we'll help you get one!</p>}
                </div>
              </div>
            </div>
          )}

          {/* Step 6: Budget & Timeline */}
          {step === 6 && (
            <div data-testid="wizard-step-budget">
              <p className="text-[10px] text-red-600 uppercase tracking-[0.2em] mb-2">Step 6 of 6</p>
              <h3 className="text-xl font-black uppercase tracking-tight mb-6">Budget & <span className="text-red-600">Timeline</span></h3>
              <div className="space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">What's your budget range?</label>
                  <div className="space-y-2">
                    {["Under Rs 10,000", "Rs 10,000 - Rs 20,000", "Rs 20,000 - Rs 35,000", "Rs 35,000+", "Not sure yet — tell me what it'll cost"].map(b => (
                      <button key={b} onClick={() => update("budget", b)}
                        className={`w-full text-left px-4 py-2.5 text-xs font-bold border transition-all ${answers.budget === b ? "bg-red-600/10 text-red-500 border-red-600" : "border-white/10 text-zinc-400 hover:border-red-600/30"}`}>
                        {b}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">When do you need it?</label>
                  <div className="flex flex-wrap gap-2">
                    {["ASAP (within a week)", "2-3 weeks", "1 month", "No rush"].map(t => (
                      <button key={t} onClick={() => update("timeline", t)}
                        className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border transition-all ${answers.timeline === t ? "bg-red-600 text-white border-red-600" : "border-white/10 text-zinc-400 hover:border-red-600/50"}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Anything else you want to tell us?</label>
                  <textarea value={answers.additionalNotes} onChange={e => update("additionalNotes", e.target.value)} rows={3} placeholder="Any specific requirements, questions, or ideas..."
                    className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none resize-none transition-colors" />
                </div>
              </div>
              {error && <p className="text-red-500 text-xs mt-3">{error}</p>}
            </div>
          )}

          {/* Step 7: Analyzing */}
          {step === 7 && (
            <div className="text-center py-16" data-testid="wizard-step-analyzing">
              <Loader2 size={40} className="text-red-600 animate-spin mx-auto mb-6" />
              <h3 className="text-xl font-black uppercase tracking-tight mb-3">Analyzing Your Requirements</h3>
              <p className="text-sm text-zinc-400">Our AI is creating your custom website proposal...</p>
            </div>
          )}

          {/* Step 8: Result */}
          {step === 8 && result && (
            <div data-testid="wizard-step-result">
              <div className="flex items-center gap-3 mb-6">
                <CheckCircle size={24} className="text-red-500" />
                <h3 className="text-xl font-black uppercase tracking-tight">Your <span className="text-red-600">Proposal</span></h3>
              </div>

              <div className="space-y-4">
                {/* Summary */}
                <div className="border border-white/10 p-4">
                  <p className="text-[10px] uppercase tracking-wider text-red-600 mb-2">Project Summary</p>
                  <p className="text-sm text-zinc-300 leading-relaxed">{result.summary}</p>
                </div>

                {/* Tiered Packages */}
                {result.packages && result.packages.length > 0 ? (
                  <div className="space-y-3">
                    <p className="text-[10px] uppercase tracking-wider text-red-600">Choose Your Package</p>
                    {result.packages.map((pkg, i) => (
                      <div key={i} className={`border p-4 relative ${pkg.recommended ? 'border-red-600 bg-red-600/5' : 'border-white/10'}`}>
                        {pkg.recommended && <div className="absolute -top-2.5 right-3 bg-red-600 text-white text-[8px] font-bold uppercase tracking-widest px-2 py-0.5">Best Value</div>}
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className={`text-sm font-black uppercase tracking-wider ${pkg.recommended ? 'text-red-500' : 'text-white'}`}>{pkg.name}</p>
                            <p className="text-[10px] text-zinc-500 mt-0.5">+ {pkg.monthly} maintenance</p>
                          </div>
                          <p className="text-xl font-black text-white">{pkg.price}</p>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {pkg.features.map((f, fi) => (
                            <span key={fi} className="text-[9px] px-2 py-0.5 border border-white/5 text-zinc-500">{f}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="border border-white/10 p-4">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Standard</p>
                      <p className="text-xl font-black text-white">{result.standardPrice}</p>
                    </div>
                    <div className="border border-red-600 bg-red-600/5 p-4 relative">
                      <div className="absolute -top-2.5 right-3 bg-red-600 text-white text-[8px] font-bold uppercase tracking-widest px-2 py-0.5">Recommended</div>
                      <p className="text-[10px] uppercase tracking-wider text-red-500 mb-2">Premium</p>
                      <p className="text-xl font-black text-white">{result.premiumPrice}</p>
                    </div>
                  </div>
                )}

                <p className="text-xs text-zinc-500">Timeline: {result.timeline}</p>

                {/* Breakdown */}
                {result.breakdown && (
                  <div className="border border-white/10 p-4">
                    <p className="text-[10px] uppercase tracking-wider text-red-600 mb-3">Standard Price Breakdown</p>
                    <div className="space-y-2">
                      {result.breakdown.map((b, i) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span className="text-zinc-400">{b.item}</span>
                          <span className="text-white font-bold">{b.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Animation Upsell */}
                <div className="border border-red-600/40 bg-gradient-to-b from-red-600/10 to-transparent p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles size={16} className="text-red-500" />
                    <p className="text-[10px] uppercase tracking-wider text-red-500 font-bold">Premium Animation Package</p>
                  </div>
                  {result.animationUpsell && <p className="text-xs text-zinc-300 leading-relaxed mb-3">{result.animationUpsell}</p>}
                  {result.animationBreakdown && (
                    <div className="space-y-1.5 mb-3">
                      {result.animationBreakdown.map((b, i) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span className="text-zinc-400">{b.item}</span>
                          <span className={`font-bold ${i === result.animationBreakdown.length - 1 ? 'text-red-500' : 'text-zinc-500 line-through'}`}>{b.price}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-[9px] text-zinc-500">Animated websites get 40% more engagement. Competitors charge Rs 50,000+ for this.</p>
                </div>

                {/* Recommended Pages */}
                {result.recommendedPages && (
                  <div className="border border-white/10 p-4">
                    <p className="text-[10px] uppercase tracking-wider text-red-600 mb-2">Recommended Pages</p>
                    <div className="flex flex-wrap gap-1.5">
                      {result.recommendedPages.map((p, i) => (
                        <span key={i} className="px-2 py-1 text-[9px] uppercase tracking-wider border border-white/10 text-zinc-400">{p}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tips */}
                {result.tips && (
                  <div className="border border-white/10 p-4">
                    <p className="text-[10px] uppercase tracking-wider text-red-600 mb-2">Expert Tips</p>
                    <ul className="space-y-1.5">
                      {result.tips.map((t, i) => (
                        <li key={i} className="text-xs text-zinc-400 leading-relaxed flex gap-2">
                          <span className="text-red-600 shrink-0">-</span> {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* WhatsApp CTA */}
                <button onClick={() => sendWhatsApp(result.whatsappMessage)} data-testid="wizard-whatsapp-send"
                  className="w-full py-4 bg-[#25D366] text-white font-bold uppercase tracking-widest text-sm flex items-center justify-center gap-2 hover:brightness-110 transition-all">
                  <Send size={16} /> Send to Kamran on WhatsApp
                </button>
                <p className="text-[9px] text-zinc-600 text-center">Opens WhatsApp with your requirements pre-filled. You can edit before sending.</p>
              </div>
            </div>
          )}

          {/* Navigation */}
          {step > 0 && step < 7 && (
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/5">
              <button onClick={goBack} className="text-xs font-bold uppercase tracking-wider text-zinc-500 hover:text-white transition-colors flex items-center gap-1">
                <ArrowLeft size={14} /> Back
              </button>
              <button onClick={goNext} disabled={!canProceed()} data-testid="wizard-next-btn"
                className="bg-red-600 text-white font-bold uppercase tracking-widest py-3 px-8 text-xs hover:bg-white hover:text-black transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2">
                {step === 6 ? <>Analyze with AI <Sparkles size={14} /></> : <>Next <ArrowRight size={14} /></>}
              </button>
            </div>
          )}

          {/* Close on result */}
          {step === 8 && (
            <div className="mt-6 text-center">
              <button onClick={onClose} className="text-xs text-zinc-500 hover:text-white transition-colors uppercase tracking-wider">Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
